import { getApiUrl } from '@/lib/api-config';

/**
 * The generic READ client: every screen that needs a record list, a record or a
 * form dropdown's reference data asks the backend API (which applies the caller's
 * permissions, company scope, soft-delete and field visibility). Nothing here talks
 * to Supabase. Writes are the generic entity API, used by each module's own store.
 */
export async function getJson<T>(path: string, fallback: string): Promise<T> {
  const response = await fetch(getApiUrl(path));
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || fallback);
  return body.data as T;
}

interface IncludeOptions {
  /** attachment_count / comment_count */
  includeCounts?: boolean;
  /** created_by_name */
  includeCreator?: boolean;
  /** the entity's roll-ups (e.g. a bill's amount_paid), computed from its child rows */
  includeRollups?: boolean;
}

function includeParam(opts: IncludeOptions): string {
  return [opts.includeCounts && 'counts', opts.includeCreator && 'creator', opts.includeRollups && 'rollups'].filter(Boolean).join(',');
}

/**
 * The company's live records of an entity type, newest first (GET /api/entities/<type>).
 * `includeCounts` adds `attachment_count` / `comment_count`; `includeCreator` adds `created_by_name`
 * (both computed on the server). `filters` narrows by the columns the entity declares filterable -- a child entity
 * (line items, ...) is always listable by its parent: `{ filters: { checklist_id } }`. For small child lists, NOT for
 * the lists screens page through (`fetchEntityPage`).
 */
export function fetchEntityList<T = Record<string, any>>(
  entityType: string,
  opts: IncludeOptions & { projectId?: string; filters?: EntityQuery['filters'] } = {}
): Promise<T[]> {
  const params = new URLSearchParams();
  if (opts.projectId) params.set('project_id', opts.projectId);
  const include = includeParam(opts);
  if (include) params.set('include', include);
  for (const [column, value] of Object.entries(filterValues(opts.filters))) params.set(`filters[${column}]`, value);
  const query = params.toString();
  return getJson<T[]>(`api/entities/${entityType}${query ? `?${query}` : ''}`, `Failed to load ${entityType} records`);
}

/**
 * THE current query of an entity list: which records the user is looking at and in what order. ONE
 * representation, used by every way of reading it -- `fetchEntityPage` (the table, one page at a time),
 * `fetchEntityExport` (every match, no page) and `fetchEntitySummary` (KPIs over every match) -- so the
 * table, the export and the KPIs can never disagree about which records they mean.
 *
 * The backend decides what is allowed: `search` needs the entity to be `searchable`; `filters` and `sort`
 * only accept the columns / keys the entity declares (anything else is a 400, never ignored).
 */
export interface EntityQuery {
  projectId?: string;
  search?: string;
  /** column -> value, or several values (any of them). Empty values are not sent. */
  filters?: Record<string, string | string[] | undefined>;
  /** a key the entity declares sortable (`sortable` in its entity-config) */
  sort?: string;
  direction?: 'asc' | 'desc';
}

export interface EntityPage<T> {
  rows: T[];
  total: number;
  hasMore: boolean;
}

function filterValues(filters: EntityQuery['filters']): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [column, value] of Object.entries(filters ?? {})) {
    const list = (Array.isArray(value) ? value : [value]).filter((v): v is string => typeof v === 'string' && v !== '');
    if (list.length > 0) out[column] = list.join(',');
  }
  return out;
}

/** The query as the body of the export / summary requests. */
function queryBody(query: EntityQuery): Record<string, unknown> {
  const filters = filterValues(query.filters);
  return {
    ...(query.projectId ? { project_id: query.projectId } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
    ...(query.sort ? { sort: query.sort, dir: query.direction ?? 'asc' } : {}),
  };
}

/**
 * The query string of ONE page of an entity query (?project_id&search&filters[col]&sort&dir&limit&offset[&include]).
 * Every endpoint that serves a page of an entity list -- the generic `/api/entities/<type>` and the specialised reads
 * built on it (e.g. `/api/selections`) -- takes exactly this, parsed by the same backend parser.
 */
export function entityQueryParams(
  query: EntityQuery,
  page: IncludeOptions & { limit: number; offset: number }
): URLSearchParams {
  const params = new URLSearchParams();
  if (query.projectId) params.set('project_id', query.projectId);
  const include = includeParam(page);
  if (include) params.set('include', include);
  if (query.search) params.set('search', query.search);
  for (const [column, value] of Object.entries(filterValues(query.filters))) params.set(`filters[${column}]`, value);
  if (query.sort) {
    params.set('sort', query.sort);
    params.set('dir', query.direction ?? 'asc');
  }
  params.set('limit', String(page.limit));
  params.set('offset', String(page.offset));
  return params;
}

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const response = await fetch(getApiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(parsed?.error || parsed?.message || fallback);
  return parsed.data as T;
}

/**
 * A write through the generic entity API (POST / PUT / DELETE /api/entities/...): resolves with the saved record (`data`),
 * rejects with the server's own message. The company and the author are never sent -- the server derives them.
 */
export async function writeEntity<T = any>(path: string, method: 'POST' | 'PUT' | 'DELETE', body: unknown, failure: string): Promise<T> {
  const response = await fetch(getApiUrl(path), {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const parsed = await response.json().catch(() => ({}));
  if (!response.ok) {
    // the server's machine-readable `code` (e.g. 'version_conflict') travels with the message, so a caller can react to it
    throw Object.assign(new Error(parsed?.error || parsed?.message || failure), { code: parsed?.code as string | undefined });
  }
  return parsed.data as T;
}

/**
 * ONE page of the company's live records matching `query` (GET /api/entities/<type>?limit=&offset=): the
 * database applies every permission / company / project / soft-delete restriction, the search and the
 * filters, orders the WHOLE result (stable tie-breaker) and then cuts the page, so consecutive pages never
 * repeat or skip a record. `total` and `hasMore` describe the matches, not the whole list.
 */
export async function fetchEntityPage<T = Record<string, any>>(
  entityType: string,
  query: EntityQuery,
  page: IncludeOptions & { limit: number; offset: number }
): Promise<EntityPage<T>> {
  const params = entityQueryParams(query, page);
  const response = await fetch(getApiUrl(`api/entities/${entityType}?${params}`));
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || `Failed to load ${entityType} records`);
  return { rows: (body.data as T[]) ?? [], total: Number(body.total ?? 0), hasMore: body.has_more === true };
}

/**
 * EVERY record matching `query` -- no page -- straight from the backend (POST /api/entities/<type>/export,
 * a read; POST only so a selection fits in the body), in the table's order. The rows are the table's own
 * columns (no names, counters or attachments). `ids` narrows the export to a selection, under the same
 * restrictions.
 */
export function fetchEntityExport<T = Record<string, any>>(
  entityType: string,
  query: EntityQuery,
  opts: { ids?: string[] } = {}
): Promise<T[]> {
  return postJson<T[]>(`api/entities/${entityType}/export`, { ...queryBody(query), ...(opts.ids ? { ids: opts.ids } : {}) }, `Failed to export ${entityType} records`);
}

/** KPIs of an entity type that declares `summary`, over EVERY record matching `query` (not the loaded page). */
export interface EntitySummary {
  total: number;
  sum: Record<string, number>;
  by: Record<string, Record<string, { count: number; sum: Record<string, number> }>>;
}

export function fetchEntitySummary(entityType: string, query: EntityQuery): Promise<EntitySummary> {
  return postJson<EntitySummary>(`api/entities/${entityType}/summary`, queryBody(query), `Failed to summarize ${entityType} records`);
}

/** One live record (GET /api/entities/<type>/:id), optionally with the same server-computed extras as the list. */
export function fetchEntity<T = Record<string, any>>(
  entityType: string,
  id: string,
  opts: IncludeOptions = {}
): Promise<T> {
  const include = includeParam(opts);
  return getJson<T>(`api/entities/${entityType}/${id}${include ? `?include=${include}` : ''}`, `Failed to load ${entityType}`);
}

/** A record's live comments with their authors (GET /api/entities/comment), oldest first. */
export function fetchEntityComments<T = Record<string, any>>(entityType: string, entityId: string): Promise<T[]> {
  const params = new URLSearchParams({ entity_type: entityType, entity_id: entityId });
  return getJson<T[]>(`api/entities/comment?${params}`, 'Failed to load comments');
}

export interface ProjectLookup {
  id: string;
  name: string;
  /** The project's address, for forms that need it (e.g. the daily log's weather). */
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  status?: string;
}

export interface TeamMemberLookup {
  /** The person inside the company (user_company_relation.local_id): what a document's vendor / assignee / client column holds. */
  id: string;
  /** The global account (users.id): the signed-in user, `created_by`. */
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  company_name: string | null;
  phone: string | null;
  role: 'admin' | 'member' | 'client' | 'vendor';
  full_name: string;
  /** vendors: the cost codes the vendor works on in this company (phase194) */
  cost_code_ids?: string[];
}

/** The company's active projects (id, name), for form dropdowns; `includeInactive` adds the finished ones a record may still point at. */
export function fetchProjectLookup(opts: { includeInactive?: boolean } = {}): Promise<ProjectLookup[]> {
  return getJson<ProjectLookup[]>(`api/lookups/projects${opts.includeInactive ? '?include_inactive=true' : ''}`, 'Failed to load projects');
}

/** The company's active people, one row each; `roles` limits them (e.g. ['admin','member']). */
export function fetchTeamMemberLookup(roles?: string[]): Promise<TeamMemberLookup[]> {
  const query = roles && roles.length > 0 ? `?roles=${encodeURIComponent(roles.join(','))}` : '';
  return getJson<TeamMemberLookup[]>(`api/lookups/team-members${query}`, 'Failed to load people');
}
